// Dashboard admin session storage.
//
// Security notes (storage review):
// - Only an OPAQUE session token issued by /api/admin/login is stored. The
//   password is never written to browser storage.
// - sessionStorage is used deliberately: it is scoped to the current tab and
//   cleared when the tab closes, so a shared/borrowed device does not keep a
//   live dashboard session around the way localStorage or a persistent cookie
//   would.
// - The server enforces expiry (ADMIN_SESSION_TTL_HOURS). We mirror the
//   `expires_at` returned by the login endpoint so the client drops stale
//   tokens proactively (and never sends an already-expired token).
// - Defence against tampering: any unparsable / malformed value is discarded.

import { logger } from "@/lib/logger";

const SESSION_KEY = "cpf_admin_session";

// Small safety margin so we do not attempt a request with a token that is
// about to expire in transit.
const EXPIRY_SKEW_MS = 30 * 1000;

function storage() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function isExpired(expiresAt) {
  if (!expiresAt) return false; // legacy token with no expiry hint: let server decide
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) return true; // corrupt value: treat as expired
  return t - EXPIRY_SKEW_MS <= Date.now();
}

// Returns { token, expiresAt } or null if there is no valid session.
export function readAdminSession() {
  const s = storage();
  if (!s) return null;
  const raw = s.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.token !== "string" || !parsed.token) {
      clearAdminSession();
      return null;
    }
    if (isExpired(parsed.expiresAt)) {
      clearAdminSession();
      return null;
    }
    return { token: parsed.token, expiresAt: parsed.expiresAt || null };
  } catch {
    // Older builds stored the bare token string. Discard it so the user
    // re-authenticates and we pick up an expiry hint.
    clearAdminSession();
    return null;
  }
}

export function writeAdminSession({ token, expires_at }) {
  const s = storage();
  if (!s || !token) return;
  try {
    s.setItem(SESSION_KEY, JSON.stringify({ token, expiresAt: expires_at || null }));
  } catch (e) {
    // Storage unavailable (private mode / quota) - session lives in memory only
    // for this tab; the user will simply be asked to log in again on reload.
    logger.debug("[adminSession] could not persist session", e);
  }
}

export function clearAdminSession() {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(SESSION_KEY);
  } catch (e) {
    logger.debug("[adminSession] could not clear session", e);
  }
}

// Milliseconds until the session expires (or null if unknown).
export function msUntilExpiry(expiresAt) {
  if (!expiresAt) return null;
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, t - EXPIRY_SKEW_MS - Date.now());
}
